/**
 * Shared PM data export utility.
 * Reused by Gantt Chart, Calendar, and future PM components.
 */

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().split('T')[0];
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function buildRows(
  data: Record<string, unknown>[],
  fieldMapping: Record<string, string | undefined>,
  visibleFields: string[],
): { headers: string[]; rows: string[][] } {
  const mappedFields = visibleFields
    .map(role => ({ role, field: fieldMapping[role] }))
    .filter((f): f is { role: string; field: string } => !!f.field);

  const headers = mappedFields.map(f => f.role);
  const rows = data.map(record =>
    mappedFields.map(f => formatValue(record[f.field]))
  );
  return { headers, rows };
}

function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportToCSV(
  data: Record<string, unknown>[],
  fieldMapping: Record<string, string | undefined>,
  visibleFields: string[],
  filename: string
): void {
  const { headers, rows } = buildRows(data, fieldMapping, visibleFields);
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(v => `"${v.replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  downloadFile(csvContent, filename.endsWith('.csv') ? filename : `${filename}.csv`, 'text/csv');
}

export function exportToTSV(
  data: Record<string, unknown>[],
  fieldMapping: Record<string, string | undefined>,
  visibleFields: string[],
  filename: string
): void {
  const { headers, rows } = buildRows(data, fieldMapping, visibleFields);
  const tsvContent = [headers.join('\t'), ...rows.map(row => row.join('\t'))].join('\n');
  downloadFile(tsvContent, filename.endsWith('.tsv') ? filename : `${filename}.tsv`, 'text/tab-separated-values');
}
